import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { fromUnixTime } from 'date-fns';
import { BalanceReportExpandContextProvider } from 'pages/Finance/BalanceReport/context/BalanceReportExpandContext';
import { flattenBalanceData } from 'pages/Finance/BalanceReport/utils/flattenBalanceData';
import { getVisibleRows } from 'pages/Finance/BalanceReport/utils/getVisibleRows';
import { DateTimeField, FunctionField } from 'shared/react-admin/Fields';
import {
  AccountAmountDataType,
  AmountDataType,
  CompanyBalanceReport,
  MerchantAccountBalanceReport,
  MerchantBalanceReport,
  ProviderBalanceReport,
} from 'types/embedded/balanceReport';

import { DatagridStyled } from './BalanceReportDatagrid.styled';
import { BalanceReportDatagridAmountCell } from './BalanceReportDatagridAmountCell';
import { BalanceReportDatagridBody } from './BalanceReportDatagridBody';
import { BalanceReportDatagridNameCell } from './BalanceReportDatagridNameCell';
import { BalanceReportDatagridRow } from './BalanceReportDatagridRow';

export type BalanceRow = {
  id: string;
  parentId?: string;
  level: number;
  type: 'company' | 'merchant' | 'provider' | 'account';
  name: string;
  currencyCode?: string;
  firstTransactionAt?: number | null;
  amountData: AccountAmountDataType | AmountDataType;
  company?: CompanyBalanceReport;
  merchant?: MerchantBalanceReport;
  provider?: ProviderBalanceReport;
  merchantAccount?: MerchantAccountBalanceReport;
  index: number;
};

const ACCOUNT_ROW_HEIGHT = 58;
const GROUP_ROW_HEIGHT = 45;
const OVERSCAN_ROWS = 12;

export const BalanceReportDatagrid = ({ data }: { data: CompanyBalanceReport[] }) => {
  const rows = useMemo(() => flattenBalanceData(data), [data]);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const toggleRow = useCallback(
    (id: string) => setExpanded((prev) => ({ ...prev, [id]: !prev[id] })),
    [],
  );
  const visibleRows = useMemo(() => getVisibleRows(rows, expanded), [rows, expanded]);
  const expandContextValue = useMemo(() => ({ expanded, toggleRow }), [expanded, toggleRow]);

  const bodyRef = useRef<HTMLTableSectionElement>(null);
  const [scrollMargin, setScrollMargin] = useState(0);

  useLayoutEffect(() => {
    if (bodyRef.current) {
      setScrollMargin(bodyRef.current.getBoundingClientRect().top + window.scrollY);
    }
  }, []);

  const virtualizer = useWindowVirtualizer({
    count: visibleRows.length,
    estimateSize: (index) =>
      visibleRows[index]?.type === 'account' ? ACCOUNT_ROW_HEIGHT : GROUP_ROW_HEIGHT,
    getItemKey: (index) => visibleRows[index]?.id ?? index,
    overscan: OVERSCAN_ROWS,
    scrollMargin,
  });

  const virtualItems = virtualizer.getVirtualItems();
  const windowRows = virtualItems.map((item) => visibleRows[item.index]);
  const topPadding = virtualItems.length ? virtualItems[0].start - scrollMargin : 0;
  const bottomPadding = virtualItems.length
    ? virtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end
    : 0;

  return (
    <BalanceReportExpandContextProvider value={expandContextValue}>
      <DatagridStyled
        body={
          <BalanceReportDatagridBody
            bodyRef={bodyRef}
            bottomPadding={bottomPadding}
            measureElement={virtualizer.measureElement}
            row={<BalanceReportDatagridRow />}
            startIndex={virtualItems[0]?.index ?? 0}
            topPadding={topPadding}
          />
        }
        bulkActionButtons={false}
        data={windowRows}
        resizableColumns={true}
        rowClick={(_id, _resource, record) => {
          const row = record as BalanceRow;
          if (row?.type !== 'account') {
            toggleRow(row.id);
          }
          return false;
        }}
      >
        <BalanceReportDatagridNameCell
          label="Компания / Мерчант / Провайдер / Счет"
          source="name"
        />

        <FunctionField
          cellClassName="resize-currencyCode"
          headerClassName="resize-currencyCode"
          label="Валюта счёта"
          render={(record) => record?.currencyCode ?? ''}
          source="currencyCode"
        />
        <FunctionField
          cellClassName="resize-firstTransactionAt"
          headerClassName="resize-firstTransactionAt"
          label="Дата начала работы"
          render={(record) => {
            if (typeof record.firstTransactionAt === 'undefined') {
              return '';
            }

            return (
              <DateTimeField
                label="Дата начала работы"
                source="firstTransactionAt"
                transform={(v: number) => fromUnixTime(v)}
              />
            );
          }}
          source="firstTransactionAt"
        />

        <BalanceReportDatagridAmountCell
          cellClassName="resize-beginPeriodBalance"
          color="text.dark"
          convertedSource="convertedBeginPeriodBalance"
          headerClassName="resize-beginPeriodBalance"
          label="Баланс на начало отчета"
          originalSource="beginPeriodBalance"
        />
        <BalanceReportDatagridAmountCell
          cellClassName="resize-paymentAmount"
          color="success.main"
          convertedSource="convertedPaymentAmount"
          headerClassName="resize-paymentAmount"
          label="Пополнения"
          originalSource="paymentAmount"
        />
        <BalanceReportDatagridAmountCell
          cellClassName="resize-payoutAmount"
          color="error.main"
          convertedSource="convertedPayoutAmount"
          headerClassName="resize-payoutAmount"
          label="Выводы"
          originalSource="payoutAmount"
        />
        <BalanceReportDatagridAmountCell
          cellClassName="resize-refundAmount"
          color="text.dark"
          convertedSource="convertedRefundAmount"
          headerClassName="resize-refundAmount"
          label="Рефанды"
          originalSource="refundAmount"
        />
        <BalanceReportDatagridAmountCell
          cellClassName="resize-chargebackAmount"
          color="text.dark"
          convertedSource="convertedChargebackAmount"
          headerClassName="resize-chargebackAmount"
          label="Чарджбеки"
          originalSource="chargebackAmount"
        />
        <BalanceReportDatagridAmountCell
          cellClassName="resize-commissionPaymentAmount"
          color="text.dark"
          convertedSource="convertedCommissionPaymentAmount"
          headerClassName="resize-commissionPaymentAmount"
          label="Комиссия за пополнения"
          originalSource="commissionPaymentAmount"
        />
        <BalanceReportDatagridAmountCell
          cellClassName="resize-commissionPayoutAmount"
          color="text.dark"
          convertedSource="convertedCommissionPayoutAmount"
          headerClassName="resize-commissionPayoutAmount"
          label="Комиссия за выводы"
          originalSource="commissionPayoutAmount"
        />
        <BalanceReportDatagridAmountCell
          cellClassName="resize-commissionRefundAmount"
          color="text.dark"
          convertedSource="convertedCommissionRefundAmount"
          headerClassName="resize-commissionRefundAmount"
          label="Комиссия за рефанды"
          originalSource="commissionRefundAmount"
        />
        <BalanceReportDatagridAmountCell
          cellClassName="resize-commissionChargebackAmount"
          color="text.dark"
          convertedSource="convertedCommissionChargebackAmount"
          headerClassName="resize-commissionChargebackAmount"
          label="Комиссия за чарджбеки"
          originalSource="commissionChargebackAmount"
        />
        <BalanceReportDatagridAmountCell
          cellClassName="resize-bankOperationPayoutAmount"
          color="error.main"
          convertedSource="convertedBankOperationPayoutAmount"
          headerClassName="resize-bankOperationPayoutAmount"
          label="Списания по банк. операциям"
          originalSource="bankOperationPayoutAmount"
        />
        <BalanceReportDatagridAmountCell
          cellClassName="resize-bankOperationPaymentAmount"
          color="success.main"
          convertedSource="convertedBankOperationPaymentAmount"
          headerClassName="resize-bankOperationPaymentAmount"
          label="Пополнения по банк. операциям"
          originalSource="bankOperationPaymentAmount"
        />
        <BalanceReportDatagridAmountCell
          cellClassName="resize-total"
          color="text.dark"
          convertedSource="convertedTotal"
          headerClassName="resize-total"
          label="Итого"
          originalSource="total"
        />
        <BalanceReportDatagridAmountCell
          cellClassName="resize-endPeriodBalance"
          color="text.dark"
          convertedSource="convertedEndPeriodBalance"
          headerClassName="resize-endPeriodBalance"
          isOriginalAlwaysVisible
          label="Сумма на конец"
          originalSource="endPeriodBalance"
        />
      </DatagridStyled>
    </BalanceReportExpandContextProvider>
  );
};
